import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';

import { AnalyticsEvent } from '@shure/shared/models';

import { AnalyticsProvider } from '../models';

@Injectable()
export class AnalyticsService implements OnDestroy {
	private readonly events$ = new Subject<AnalyticsEvent>();
	private readonly destroy$ = new Subject<void>();
	private readonly localStorageKey = 'analyticsOpted';
	private readonly _analytics$ = new BehaviorSubject<boolean>(this.isAnalyticsOpted());

	public readonly analyticsOptStatus$ = this._analytics$
		.asObservable()
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));

	constructor(private provider: AnalyticsProvider) {
		this.startEventProcessingSubscription();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public optIn(): void {
		this._analytics$.next(true);
		this.provider.optIn();
	}

	public optOut(): void {
		this._analytics$.next(false);
		this.provider.optOut();
	}

	public isAnalyticsOpted(): boolean {
		return localStorage.getItem(this.localStorageKey) === 'true';
	}

	/**
	 * Enqueues incoming events into an observable
	 * which are then processed asynchronously
	 */
	public trackEvent<T extends AnalyticsEvent>(event: T): void {
		this.events$.next(event);
	}

	/**
	 * Starts listening on the observable and sends
	 * incoming events to the provider
	 */
	private startEventProcessingSubscription(): void {
		this.events$.pipe(takeUntil(this.destroy$)).subscribe((analyticsEvent: AnalyticsEvent) => {
			this.provider.trackEvent(analyticsEvent);
		});
	}
}
